import styles from './chart.scss';
import { NormalizedSeries } from 'types';
import { round } from 'helpers/math';

export const CHART_COLORS = [
  '#ffc425',
  '#003f5c',
  '#d45087',
  '#00b159',
  '#ffa600',
  '#2f4b7c',
  '#a05195',
  '#f37735',
  '#f95d6a',
  '#ff7c43',
  '#d11141',
  '#00aedb',
  '#665191',
  '#e6194b',
  '#3cb44b',
  '#ffe119',
  '#4363d8',
  '#f58231',
  '#911eb4',
  '#46f0f0',
  '#f032e6',
  '#bcf60c',
  '#fabebe',
  '#008080',
  '#e6beff',
  '#9a6324',
  '#fffac8',
  '#800000',
  '#aaffc3',
  '#808000',
  '#ffd8b1',
  '#000075',
  '#808080'
];

export const CHART_TARGET_TYPE = 'chart-series';
export const NAVIGATOR_HEIGHT = parseInt(styles.navigatorHeight);
export const LABEL_WIDTH = 20;
export const DEFAULT_CHART_HEIGHT = 350;
export const SERIES_DIVIDER_HEIGHT = 4;
export const CHART_PADDING = 10;

export type Point = [number, number];

export type SeriesConfig = { label: string, suffix: string };

export const SERIES_CONFIG: { [key: string]: SeriesConfig } = {
  gps: {
    label: 'Speed',
    suffix: 'm/s'
  },
  pressure: {
    label: 'Pressure',
    suffix: 'mB'
  },
  rssi: {
    label: 'RSSI',
    suffix: 'mDb'
  }
};

export function getSeriesLabel(seriesId: string) {
  const config = SERIES_CONFIG[seriesId];
  return config ? config.label : seriesId;
}

export function getSeriesValue(
  series: NormalizedSeries,
  normalizedY: number,
  noSuffix?: boolean
): string | number {
  // pointY is normalized so need to scale it up to get real value
  let displayValue = normalizedY * (series.maxY - series.minY) + series.minY;

  if (series.scale) displayValue *= series.scale;
  displayValue = round(displayValue, series.decimals || 1);
  
  if (series.format) displayValue = displayValue + ' ' + series.format;
  return displayValue;
}

export function getNormalizeSeries(series: { id: string, data: Point[] }[]): NormalizedSeries[] {
  const res: NormalizedSeries[] = [];
  console.log(series);
  let minX = Infinity,
    maxX = -Infinity;
  let i = 0;
  for (let s of series) {
    let minY = Infinity;
    let maxY = -Infinity;
    // const p = series.data.slice();
    for (let point of s.data) {
      // Clone point so we can normalize
      if (point[0] < minX) minX = point[0];
      if (point[0] > maxX) maxX = point[0];
      if (point[1] < minY) minY = point[1];
      if (point[1] > maxY) maxY = point[1];
    }

    res.push({
      ...s,
      color: CHART_COLORS[i++],
      minY,
      maxY,
      points: s.data
    });
  }

  // Normalize x axis
  let rangeX = maxX - minX;
  for (let s of res) {
    const rangeY = s.maxY - s.minY;
    s.minX = minX;
    s.maxX = maxX;
    s.points = s.points.map(([x, y]) => [(x - minX) / rangeX, (y - s.minY) / rangeY]);
  }
  res.minX = minX;
  res.maxX = maxX;

  return res;
}

export function filterMaxPoints(points: Point[], maxPoints: number) {
  return maxNumberOfPoints(points, maxPoints);
}

export function filterRange(points: Point[], range: number) {
  if (!range || (range[0] <= 0 && range[1] >= 1)) return points;
  return points.filter(
    (p, i) =>
      points[Math.min(points.length - 1, i + 1)][0] >= range[0] &&
      points[Math.max(0, i - 1)][0] <= range[1]
  );
}

function getSlope(
  points: Point[],
  start: number = 0,
  end: number = points.length
): [number, number] {
  let slope = 0;
  for (let i = start + 1; i < end; i++) {
    const p1 = points[i - 1];
    const p2 = points[i];

    slope += (p2[1] - p1[1]) / (p2[0] - p1[0]);
  }

  return slope / (end - start);
}

export function maxNumberOfPoints(points: Point[], maxPoints: number) {
  if (points.length <= maxPoints) return points;
  let newPoints = [];
  let step = Math.floor(points.length / maxPoints);
  const len = points.length;
  for (let i = 0; i < len; i += step) {
    // let sum = 0;
    const stepEnd = Math.min(len, i + step);
    const slope = getSlope(points, i, stepEnd);
    let p = points[i];

    // Finding the best point by using the slope,
    // if negative slope then pick the lowest point if positive then pig highest point
    for (let j = i; j < stepEnd; j++) {
      if (slope < 0 && p[1] < points[j][1]) p = points[j];
      else if (slope > 0 && p[1] > points[j][1]) p = points[j];
      // sum += points[j][1];
    }
    // newPoints.push([points[i][0], sum / step]);
    newPoints.push(p);
  }

  return newPoints;
}
